import React, { memo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import styles from '../../DataExtractionTemplate.module.scss';
import { DataExtractionTemplateState } from '../../DataExtractionTemplate';
import EditableField from '../EditableField';
import {
  validate,
  NO_EQUIVALENT_VALUE,
} from '../../../components/editableFieldValidation';
import ConfirmationDialog from '../ConfirmationDialog';
import { useHidingDefaultFields } from '../../useHidingDefaultFields';
import { useCheckSuggestions } from '../../utils';
import { SparkleWithTooltip } from 'concepts/AutoDocExtraction/Sparkle/Sparkle';
import {
  Heading,
  Text,
  Icon,
  IconList,
  Link,
  FormControl,
  Checkbox,
  Button,
} from 'components/core';
import {
  CustomFieldSupportedSuggestion,
  HiddenFieldCategory,
} from 'types/DataExtraction';

const RESERVED_FIELD_NAMES: string[] = ['design'];
const MAX_LENGTH = 255;
type Props = {
  hasExtractedData?: boolean;
  useSuggestions: boolean;
  supportedSuggestions: CustomFieldSupportedSuggestion[];
};

const MethodsPanel = ({
  hasExtractedData = false,
  useSuggestions,
  supportedSuggestions,
}: Props) => {
  const [modal, setModal] = useState<React.ReactElement | null>();
  const { getValues } = useFormContext();
  const additionalFields = useFieldArray<DataExtractionTemplateState>({
    name: 'methods.additionalFields',
  });
  const { handleChange, isChecked } = useHidingDefaultFields(
    HiddenFieldCategory.METHODS
  );
  const sectionClassnames = classNames({
    [styles.Panel]: true,
    [styles.WithinAccordion]: true,
  });

  const checkSuggestions = useCheckSuggestions(
    supportedSuggestions,
    'study_design'
  );

  return (
    <section className={sectionClassnames}>
      {modal}
      <div className={styles.details}>
        <Heading type="h5" component="h4" variant="neutral">
          Method details
        </Heading>
        <ul>
          <li>
            <FormControl
              actions={
                <>
                  <Button
                    iconOnly
                    className={styles.InfoIconButton}
                    isNaked={true}
                    size="xs"
                    data-pendo-key="det-study-design-help"
                    aria-label="More info about study design"
                    onClick={() => undefined}
                  >
                    <Icon icon={IconList.light.faCircleInfo} size="xs" />
                  </Button>
                  {useSuggestions && (
                    <SparkleWithTooltip label="Study design" />
                  )}
                </>
              }
            >
              <Checkbox
                name="design"
                value="design"
                checked={isChecked('design')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Study design
                </Text>
              </Checkbox>
            </FormControl>
          </li>
          <li>
            <FormControl
              actions={
                <Button
                  iconOnly
                  className={styles.InfoIconButton}
                  isNaked={true}
                  size="xs"
                  data-pendo-key="det-methods-group-help"
                  aria-label="More info about group"
                  onClick={() => undefined}
                >
                  <Icon icon={IconList.light.faCircleInfo} size="xs" />
                </Button>
              }
            >
              <Checkbox
                name="group"
                value="group"
                checked={isChecked('group')}
                onChange={handleChange}
              >
                <Text component="span" variant="medium">
                  Group
                </Text>
              </Checkbox>
            </FormControl>
          </li>
        </ul>
      </div>

      <div className={styles.details}>
        <Heading type="h5" component="h4" variant="neutral">
          Additional data
        </Heading>
        {additionalFields.fields.map((field, index) => {
          return (
            <EditableField
              key={field.id}
              index={index}
              name="methods.additionalFields"
              onDelete={() => {
                const { id } = getValues(`methods.additionalFields.${index}`);

                if (id && hasExtractedData) {
                  setModal(
                    <ConfirmationDialog
                      onConfirm={() => additionalFields.remove(index)}
                      onClose={() => setModal(null)}
                    />
                  );
                } else {
                  additionalFields.remove(index);
                }
              }}
              validate={(value, existingFields) => {
                return validate(
                  value,
                  existingFields,
                  RESERVED_FIELD_NAMES,
                  NO_EQUIVALENT_VALUE,
                  MAX_LENGTH
                );
              }}
              suggestable={useSuggestions ? checkSuggestions : undefined}
            />
          );
        })}
        <Link
          onClick={() => additionalFields.append({ name: '' })}
          variant="primary"
          className={styles.AddButton}
        >
          <Icon icon={IconList.light.faPlus} /> Add additional data
        </Link>
      </div>
    </section>
  );
};

export default memo(MethodsPanel);
